export type PromoLinks = {
  whatsappLink: string;
  websiteLink: string;
};

export type AffiliateStats = {
  visitsCount: number;
  signupsCount: number;
  conversionsCount: number;
  earnings: number;
};

export const affiliateSubtitle = (
  loading: boolean,
  stats: AffiliateStats | null,
): string | undefined => {
  if (loading) return "Loading earnings...";
  if (!stats) return "Earn 20% recurring commission";
  if (stats.earnings > 0) {
    const payingText = `${stats.conversionsCount} paying referral${
      stats.conversionsCount === 1 ? "" : "s"
    }`;
    return stats.earnings >= 50
      ? `$${stats.earnings}/mo earnings (eligible for payout) · ${payingText}`
      : `$${stats.earnings}/mo earnings · ${payingText}`;
  }
  if (stats.signupsCount > 0) {
    return `$0/mo earnings · ${stats.signupsCount} signup${
      stats.signupsCount === 1 ? "" : "s"
    }`;
  }
  if (stats.visitsCount > 0) {
    return `$0/mo earnings · ${stats.visitsCount} visit${
      stats.visitsCount === 1 ? "" : "s"
    }`;
  }
  return "$0/mo earnings · 20% recurring commission";
};

export type PromoLanguage = {
  id: string;
  label: string;
  isRtl: boolean;
  format: (links: PromoLinks) => string;
};

export const defaultPromoLanguageId = "en";

export const promoLanguages: readonly PromoLanguage[] = [
  {
    id: "en",
    label: "English",
    isRtl: false,
    format: ({ whatsappLink, websiteLink }) =>
      `Meet prompt2bot – an incredible platform where you simply describe what AI agent you need in plain words, and it builds it for you.

Who is it for?

- Anyone looking for a personal AI assistant
- Business owners who need a representative for their website or WhatsApp
- Building simple automations – cheaper than Make. You create a coder agent and tell it what the automation should do in plain language, with zero setup or downloads on your computer

Pricing: You can start for free in your first month, and then starting from $15/month. Often the free tier is enough to build a complete automation!

Bonus: You can do the entire building process directly on WhatsApp with the system's AI, with no sign-up required: ${whatsappLink}

${websiteLink}`,
  },
  {
    id: "he",
    label: "עברית (Hebrew)",
    isRtl: true,
    format: ({ whatsappLink, websiteLink }) =>
      `הכירו את prompt2bot - פלטפורמה ישראלית מופלאה שבה פשוט מתארים במילים איזה סוכן אתם צריכים, והיא בונה אותו עבורכם.

למי המערכת מתאימה?

- אנשים שרוצים עוזר אישי
- בעלי עסק שרוצים נציג לעסק, באתר או בווצאפ
- בניית אוטומציות פשוטות - יותר זול ממייק. מייצרים סוכן מתכנת ורק אומרים לו מה האוטומציה צריכה לעשות בעברית פשוטה, בלי להוריד שום דבר למחשב

מבחינת עלויות - אפשר להתחיל בחינם בחודש הראשון ואז החל מ-15$ בחודש. לפעמים החינמי מספיק אפילו לבניית אוטומציה שלמה ולא צריך יותר.

והבונוס - את כל תהליך הבניה אפשר לעשות ישירות בווצאפ מול ה-AI של המערכת, בלי הרשמה: ${whatsappLink}

${websiteLink}`,
  },
  {
    id: "es",
    label: "Español (Spanish)",
    isRtl: false,
    format: ({ whatsappLink, websiteLink }) =>
      `Conoce prompt2bot – una plataforma increíble donde simplemente describes con tus propias palabras qué agente de IA necesitas y lo construye para ti.

¿Para quién es?

- Personas que quieren un asistente personal de IA
- Dueños de negocios que necesitan un representante para su sitio web o WhatsApp
- Crear automatizaciones sencillas – más económico que Make. Creas un agente programador y le dices lo que debe hacer la automatización en lenguaje natural, sin descargar nada en tu ordenador

Precios: Puedes empezar gratis el primer mes, y luego desde solo $15/mes. ¡A menudo el plan gratuito es suficiente para construir una automatización completa!

Y el bonus: Puedes hacer todo el proceso de creación directamente en WhatsApp chateando con la IA, sin necesidad de registrarte: ${whatsappLink}

${websiteLink}`,
  },
  {
    id: "fr",
    label: "Français (French)",
    isRtl: false,
    format: ({ whatsappLink, websiteLink }) =>
      `Découvrez prompt2bot – une plateforme incroyable où vous décrivez simplement en quelques mots l'agent IA dont vous avez besoin, et elle le construit pour vous.

À qui s'adresse-t-elle ?

- Celles et ceux qui recherchent un assistant personnel IA
- Les entrepreneurs qui souhaitent un représentant pour leur site web ou WhatsApp
- Créer des automatisations simples – plus abordable que Make. Vous créez un agent développeur et lui expliquez en langage naturel ce qu'il doit faire, sans rien installer sur votre ordinateur

Tarifs : Vous pouvez commencer gratuitement le premier mois, puis à partir de seulement 15 $/mois. Le forfait gratuit suffit souvent pour concevoir une automatisation complète !

En bonus : Vous pouvez réaliser l'ensemble du processus de création directement sur WhatsApp en échangeant avec l'IA, sans inscription préalable : ${whatsappLink}

${websiteLink}`,
  },
  {
    id: "de",
    label: "Deutsch (German)",
    isRtl: false,
    format: ({ whatsappLink, websiteLink }) =>
      `Lerne prompt2bot kennen – eine fantastische Plattform, auf der du einfach in eigenen Worten beschreibst, welchen KI-Agenten du brauchst, und sie baut ihn für dich.

Für wen ist es geeignet?

- Alle, die einen persönlichen KI-Assistenten suchen
- Unternehmer, die einen Kundenservice-Agenten für ihre Website oder WhatsApp benötigen
- Einfache Automatisierungen erstellen – günstiger als Make. Du erstellst einen Programmier-Agenten und sagst ihm in einfacher Sprache, was die Automation tun soll – ohne etwas herunterladen zu müssen

Preise: Starte im ersten Monat kostenlos, danach ab nur 15 $/Monat. Oft reicht der kostenlose Tarif bereits aus, um eine vollständige Automation zu erstellen!

Der Bonus: Der gesamte Erstellungsprozess kann direkt über WhatsApp im Chat mit der KI durchgeführt werden, ganz ohne Registrierung: ${whatsappLink}

${websiteLink}`,
  },
  {
    id: "pt",
    label: "Português (Portuguese)",
    isRtl: false,
    format: ({ whatsappLink, websiteLink }) =>
      `Conheça o prompt2bot – uma plataforma incrível onde você simplesmente descreve com suas próprias palavras qual agente de IA precisa, e ela cria para você.

Para quem é indicado?

- Pessoas que buscam um assistente pessoal de IA
- Proprietários de empresas que precisam de um atendente para seu site ou WhatsApp
- Criação de automações simples – mais barato que o Make. Você cria um agente programador e diz o que a automação deve fazer em linguagem simples, sem precisar baixar nada no computador

Preços: Você pode começar gratuitamente no primeiro mês e depois a partir de apenas $15/mês. Muitas vezes, o plano gratuito é suficiente para criar uma automação completa!

E o bônus: Todo o processo de criação pode ser feito diretamente pelo WhatsApp conversando com a IA, sem necessidade de cadastro: ${whatsappLink}

${websiteLink}`,
  },
  {
    id: "ru",
    label: "Русский (Russian)",
    isRtl: false,
    format: ({ whatsappLink, websiteLink }) =>
      `Познакомьтесь с prompt2bot — потрясающей платформой, где вы просто описываете словами, какой ИИ-агент вам нужен, и она создаёт его для вас.

Кому подходит сервис?

- Тем, кто хочет персонального ИИ-ассистента
- Владельцам бизнеса, которым нужен представитель на сайте или в WhatsApp
- Создание простых автоматизаций — выгоднее, чем Make. Вы создаёте агента-программиста и просто говорите ему обычными словами, что должна делать автоматизация, без установки программ на компьютер

Стоимость: Первый месяц можно начать бесплатно, далее от $15 в месяц. Зачастую бесплатного тарифа достаточно для создания полноценной автоматизации!

Бонус: Весь процесс создания можно пройти прямо в WhatsApp в диалоге с ИИ, без предварительной регистрации: ${whatsappLink}

${websiteLink}`,
  },
  {
    id: "ar",
    label: "العربية (Arabic)",
    isRtl: true,
    format: ({ whatsappLink, websiteLink }) =>
      `تعرّف على prompt2bot – منصة رائعة تتيح لك ببساطة وصف وكيل الذكاء الاصطناعي الذي تحتاجه بكلماتك، لتقوم ببنائه لك.

لمن هذه المنصة؟

- من يبحثون عن مساعد شخصي بالذكاء الاصطناعي
- أصحاب الأعمال الراغبين في وجود ممثل لخدمة العملاء على موقعهم أو عبر واتساب
- بناء أتمتة بسيطة – أوفر من Make. تنشئ وكيلاً مبرمجاً وتخبره بما يجب أن تفعله الأتمتة بلغة بسيطة، دون الحاجة لتحميل أي شيء على جهازك

الأسعار: يمكنك البدء مجاناً في الشهر الأول، ثم تبدأ الباقات من 15 دولاراً شهرياً فقط. وفي كثير من الأحيان تكون الباقة المجانية كافية لبناء أتمتة كاملة!

والميزة الإضافية: يمكنك إتمام عملية البناء بالكامل مباشرة عبر واتساب من خلال التحدث مع الذكاء الاصطناعي، ودون الحاجة لتسجيل مسبق: ${whatsappLink}

${websiteLink}`,
  },
];

export const getPromoLanguage = (id: string): PromoLanguage =>
  promoLanguages.find((l) => l.id === id) ?? promoLanguages[0];
